import { useState, useMemo, useCallback, useEffect } from 'react';
import { doc, getDoc, getDocFromServer, setDoc } from 'firebase/firestore';
import type { Calendar13Config, Day13, Month13 } from '@/lib/calendar13Types';
import { MONTH_NAMES, WEEKDAY_NAMES } from '@/lib/calendar13Types';
import { gregorianToDay13, getMonth13, getCurrentGalacticYear, formatDay13, getToday13 } from '@/lib/calendar13';
import { DEFAULT_CONFIG } from '@/lib/calendar13Types';
// Do not import db at module scope to avoid circular import issues
const getDb = async () => {
  const { db } = await import('@/lib/firebase');
  return db;
};

const CAL_CONFIG_COLLECTION = 'calendarConfig';
const CAL_CONFIG_DOC_ID = 'shared';

const shouldDisableFirestore = () =>
  typeof window !== 'undefined' && window.localStorage?.getItem('disableFirestore') === '1';

const getConfigDocRef = async () => {
  const db = await getDb();
  if (!db) throw new Error('Firestore is not configured.');
  return doc(db, CAL_CONFIG_COLLECTION, CAL_CONFIG_DOC_ID);
};

const logFirestoreWrite = (detail: Record<string, unknown>) => {
  try {
    console.info('[firestore:write] calendarConfig', detail);
  } catch {
    // ignore logging errors
  }
};

const normalizeCalendarConfig = (value: unknown): Calendar13Config => {
  if (!value || typeof value !== 'object') {
    return DEFAULT_CONFIG;
  }

  const record = value as Partial<Calendar13Config>;
  const yearStartMode = record.yearStartMode === 'fixed' || record.yearStartMode === 'lunar'
    ? record.yearStartMode
    : DEFAULT_CONFIG.yearStartMode;
  const epochYear = typeof record.epochYear === 'number' && Number.isFinite(record.epochYear) && record.epochYear >= 1
    ? record.epochYear
    : DEFAULT_CONFIG.epochYear;
  const fixedStartDate = record.fixedStartDate
    && typeof record.fixedStartDate.month === 'number'
    && typeof record.fixedStartDate.day === 'number'
    && record.fixedStartDate.month >= 1
    && record.fixedStartDate.month <= 12
    && record.fixedStartDate.day >= 1
    && record.fixedStartDate.day <= 31
    ? { month: record.fixedStartDate.month, day: record.fixedStartDate.day }
    : DEFAULT_CONFIG.fixedStartDate;

  return {
    yearStartMode,
    fixedStartDate,
    epochYear,
    lunarReferenceDate: (record as any).lunarReferenceDate || DEFAULT_CONFIG.lunarReferenceDate,
    leapYearRule: (record as any).leapYearRule || DEFAULT_CONFIG.leapYearRule,
  };
};

export function useCalendar13() {
  const [config, setConfig] = useState<Calendar13Config>(() => {
    const saved = localStorage.getItem('calendar13-config');
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        return normalizeCalendarConfig(parsed);
      } catch (error) {
        console.warn('Failed to parse saved calendar config, falling back to defaults.', error);
      }
    }
    return DEFAULT_CONFIG;
  });
  
  const [selectedYear, setSelectedYear] = useState(() => getCurrentGalacticYear(config));
  const [selectedMonth, setSelectedMonth] = useState(() => getToday13(config).month || 1);
  const [selectedDay, setSelectedDay] = useState<Day13 | null>(null);
  
  const today = useMemo(() => getToday13(config), [config]);
  
  const currentMonth = useMemo(() => {
    // Month 14 is "The Drift" - not a real month, return month 13 as fallback for data
    const monthToFetch = selectedMonth === 14 ? 13 : selectedMonth;
    return getMonth13(selectedYear, monthToFetch, config);
  }, [selectedYear, selectedMonth, config]);
  
  const allMonths = useMemo(() => 
    MONTH_NAMES.map((_, idx) => getMonth13(selectedYear, idx + 1, config)),
    [selectedYear, config]
  );
  
  const updateConfig = useCallback((updates: Partial<Calendar13Config>) => {
    const newConfig = { ...config, ...updates };
    setConfig(newConfig);
    localStorage.setItem('calendar13-config', JSON.stringify(newConfig));
    (async () => {
      const db = await getDb();
      if (!shouldDisableFirestore() && db) {
        try {
          logFirestoreWrite({ action: 'saveConfig' });
          void setDoc(await getConfigDocRef(), newConfig, { merge: true });
        } catch {
          // ignore network errors
        }
      }
    })();
    // Recalculate year based on new config
    setSelectedYear(getCurrentGalacticYear(newConfig));
  }, [config]);

  useEffect(() => {
    let isActive = true;
    (async () => {
      const db = await getDb();
      if (!db || shouldDisableFirestore()) return;
      const ref = await getConfigDocRef();
      const fetch = async () => {
        try {
          const snapshot = await getDocFromServer(ref);
          if (!snapshot.exists()) return;
          const remote = normalizeCalendarConfig(snapshot.data());
          if (!isActive) return;
          setConfig(remote);
          localStorage.setItem('calendar13-config', JSON.stringify(remote));
          setSelectedYear(getCurrentGalacticYear(remote));
          return;
        } catch {
          // fallback to cache
        }
        try {
          const snapshot = await getDoc(ref);
          if (!snapshot.exists() || !isActive) return;
          const remote = normalizeCalendarConfig(snapshot.data());
          setConfig(remote);
          localStorage.setItem('calendar13-config', JSON.stringify(remote));
          setSelectedYear(getCurrentGalacticYear(remote));
        } catch {
          // ignore
        }
      };

      void fetch();
    })();

    return () => {
      isActive = false;
    };
  }, []);
  const nextMonth = useCallback(() => {
    if (selectedMonth === 14) {
      setSelectedMonth(1);
      setSelectedYear(y => y + 1);
    } else {
      setSelectedMonth(m => m + 1);
    }
  }, [selectedMonth]);
  
  const prevMonth = useCallback(() => {
    if (selectedMonth === 1) {
      setSelectedMonth(14);
      setSelectedYear(y => y - 1);
    } else {
      setSelectedMonth(m => m - 1);
    }
  }, [selectedMonth]);
  
  const goToToday = useCallback(() => {
    const todayDate = getToday13(config);
    setSelectedYear(todayDate.year);
    setSelectedMonth(todayDate.month || 1);
    setSelectedDay(todayDate);
  }, [config]);
  
  const selectDay = useCallback((day: Day13) => {
    setSelectedDay(day);
  }, []);
  
  return {
    config,
    updateConfig,
    selectedYear,
    setSelectedYear,
    selectedMonth,
    setSelectedMonth,
    selectedDay,
    selectDay,
    today,
    currentMonth,
    allMonths,
    goToToday,
    nextMonth,
    prevMonth,
  };
}
